import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * ColumnSelectorModal
 * Shows all mis_fields for the selected customer as checkboxes (all pre-checked).
 * User can deselect columns they don't want in the downloaded report.
 *
 * Props:
 *   isOpen       - bool
 *   columns      - array of { key, column_name } from customer.mis_fields
 *   onConfirm    - fn(selectedKeys: string[])
 *   onClose      - fn()
 */
const ColumnSelectorModal = ({ isOpen, columns = [], onConfirm, onClose }) => {
  const [selected, setSelected] = useState({});

  // Whenever the modal opens or columns change, reset all to checked
  useEffect(() => {
    if (isOpen) {
      const initial = {};
      columns.forEach((col) => {
        initial[col.key] = true;
      });
      setSelected(initial);
    }
  }, [isOpen, columns]);

  if (!isOpen) return null;

  const toggleAll = (checked) => {
    const updated = {};
    columns.forEach((col) => {
      updated[col.key] = checked;
    });
    setSelected(updated);
  };

  const toggle = (key) => {
    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleConfirm = () => {
    const selectedKeys = columns
      .filter((col) => selected[col.key])
      .map((col) => col.key);
    onConfirm(selectedKeys);
  };

  const allChecked = columns.length > 0 && columns.every((col) => selected[col.key]);
  const someChecked = columns.some((col) => selected[col.key]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="text-base font-semibold text-gray-800">Select Columns to Download</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Select all toggle */}
        <div className="px-5 py-3 border-b bg-gray-50">
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={(e) => toggleAll(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer"
            />
            Select All
          </label>
        </div>

        {/* Column list */}
        <div className="overflow-y-auto flex-1 px-5 py-3">
          {columns.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">No columns available.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
              {columns.map((col) => (
                <label
                  key={col.key}
                  className="flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 min-w-0"
                >
                  <input
                    type="checkbox"
                    checked={!!selected[col.key]}
                    onChange={() => toggle(col.key)}
                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 cursor-pointer"
                  />
                  <span className="uppercase truncate" title={col.column_name}>
                    {col.column_name}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!someChecked}
            className="px-4 py-2 text-sm rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Download
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColumnSelectorModal;
